// Top-level error boundary. Catches uncaught render errors anywhere in the
// tree and shows a friendly fallback instead of React's blank-screen default.
//
// Also POSTs to /api/client-errors (best-effort) so the Ops tab sees
// client-side crashes alongside worker errors. If the POST itself fails we
// swallow it — the fallback UI is the important part.
//
// React Error Boundaries must be class components; no hooks equivalent.
import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode };
type State = { err: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  override componentDidCatch(err: Error, info: ErrorInfo) {
    // Fire-and-forget so we see client crashes in analytics_events.
    try {
      fetch("/api/client-errors", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          message: err.message,
          stack: err.stack?.slice(0, 4000) ?? null,
          componentStack: info.componentStack?.slice(0, 2000) ?? null,
          path: window.location.pathname,
          ua: window.navigator.userAgent,
        }),
        credentials: "include",
      }).catch(() => { /* ignore */ });
    } catch { /* ignore */ }
    // Also log locally so the devtools console shows it.
    console.error("uncaught render error", err, info);
  }

  handleReset = () => {
    this.setState({ err: null });
  };

  override render() {
    if (!this.state.err) return this.props.children;
    return <Fallback error={this.state.err} onReset={this.handleReset} />;
  }
}

function Fallback({ error, onReset }: { error: Error; onReset: () => void }) {
  return (
    <div className="min-h-dvh flex items-center justify-center px-4 safe-top safe-bottom">
      <div className="max-w-md w-full px-card-hi text-center">
        <p className="text-[11px] uppercase tracking-[0.22em] text-px-text-muted">
          Something broke
        </p>
        <h1 className="px-heading text-xl font-bold mt-3">
          Sorry — this page crashed.
        </h1>
        <p className="mt-3 text-sm text-px-text-soft">
          Your data is safe. Refresh or head home; if it keeps happening, email{" "}
          <a className="text-px-green underline" href="mailto:jorgen@larsen.zone">
            jorgen@larsen.zone
          </a>{" "}
          and we'll sort it out.
        </p>

        <details className="mt-4 text-left">
          <summary className="text-[11px] uppercase tracking-widest text-px-text-muted cursor-pointer">
            Technical details
          </summary>
          <pre className="mt-2 text-[11px] text-px-text-muted whitespace-pre-wrap break-all font-mono">
            {error.message}
          </pre>
        </details>

        <div className="mt-5 flex gap-2 justify-center">
          <button
            type="button"
            className="px-btn-primary text-xs"
            onClick={() => window.location.reload()}
          >
            Refresh
          </button>
          <a href="/" className="px-btn-ghost text-xs" onClick={onReset}>
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
